import { Component, effect, inject, OnInit } from '@angular/core';
import { CommuneStore } from '../../../stores/commune/commune.state';
import { GlobalsStore } from '../../../stores/globals.state';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MenuItem } from 'primeng/api';
import { Commune, RadarConfiguration } from '../../../models/commune';
import { CheckboxModule } from 'primeng/checkbox';
import { ColorsetDropdownComponent } from '../../atoms/colorset-dropdown/colorset-dropdown.component';
import { DividerModule } from 'primeng/divider';
import { DropdownModule } from 'primeng/dropdown';
import { FormFieldWrapperComponent } from '../form-field-wrapper/form-field-wrapper.component';
import { HeaderComponent } from '../../atoms/header/header.component';
import { NgIf } from '@angular/common';
import { PaginatorModule } from 'primeng/paginator';
import { NgxSliderComponent } from '../../../ngx-slider/ngx-slider.component';

@Component({
  selector: 'app-configuration-chart-radar',
  standalone: true,
  imports: [
    CheckboxModule,
    ColorsetDropdownComponent,
    DividerModule,
    DropdownModule,
    FormFieldWrapperComponent,
    HeaderComponent,
    NgIf,
    PaginatorModule,
    ReactiveFormsModule,
    NgxSliderComponent,
  ],
  templateUrl: './configuration-chart-radar.component.html',
  styleUrl: './configuration-chart-radar.component.scss',
})
export class ConfigurationChartRadarComponent implements OnInit {
  readonly communeStore = inject(CommuneStore);
  readonly globalStore = inject(GlobalsStore);
  form: FormGroup = new FormGroup({});
  initialized = false;
  items: MenuItem[] = [];
  activeCommune: Commune | null = null;
  radarConfiguration?: RadarConfiguration;

  constructor(private fb: FormBuilder) {
    effect(() => {
      this.communeSetup();
    });
  }

  /**
   * Sets up the active commune configuration and initializes related properties.
   * This method checks if an active commune is set and assigns it from the global store if not.
   * It ensures the commune's atlas configuration is loaded and assigns it with a base configuration if missing.
   * Additionally, it initializes the radar configuration and prepares the form setup if required.
   *
   * @return {void} This method does not return a value.
   */
  communeSetup(): void {
    if (!this.activeCommune) {
      this.activeCommune = this.globalStore.activeCommune();
      if (!this.activeCommune) return;
      if (!this.activeCommune.atlasConfig) {
        this.activeCommune.atlasConfig = this.communeStore.baseConfig();
      }
      this.radarConfiguration = this.activeCommune.atlasConfig.radar;
    }
    if (this.activeCommune && !this.initialized) {
      this.initForm();
    }
  }

  /**
   * Lifecycle hook that is called after Angular has initialized all data-bound properties of a directive.
   * This method initializes the necessary setup by invoking the `communeSetup` method.
   *
   * @return {void} Does not return any value.
   */
  ngOnInit(): void {
    this.communeSetup();
  }

  /**
   * Initializes the form with predefined configuration settings.
   * Sets default values for form fields if configurations are missing.
   * Subscribes to value changes in the form to trigger form submission.
   * Marks the form as initialized upon initialization.
   *
   * @return {void} Does not return a value.
   */
  initForm(): void {
    this.form = this.fb.group({
      colorset: [this.radarConfiguration?.colorset],
      displayValues: [this.radarConfiguration?.displayValues || false],
      dotSize: [this.radarConfiguration?.dotSize || 1],
      featureOpacity: [this.radarConfiguration?.featureOpacity ?? 0.5],
      lineTension: [this.radarConfiguration?.lineTension || 0],
    });

    this.form.valueChanges.subscribe(() => {
      this.submit();
    });

    this.initialized = true;
  }

  /**
   * Submits the form data and updates the active commune's radar configuration.
   * Merges the current form values into the radar configuration of the active commune
   * and updates the commune store with the new data.
   *
   * @return {void} Does not return a value.
   */
  submit(): void {
    if (!this.activeCommune) return;

    this.activeCommune.atlasConfig.radar = {
      ...this.activeCommune.atlasConfig.radar,
      ...this.form.value,
    };
    this.communeStore.update(this.activeCommune);
  }
}
